import React, { ReactNode, useEffect, useState } from 'react';
import qs from 'qs';
import RangeSliderRefinement from './RangeSliderRefinement/RangeSliderRefinement';
import { CheckboxRefinement } from './CheckboxRefinement';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import { ToggleButtonRefinement } from './ToggleButtonRefinement/ToggleButtonRefinement';
import { Carousel } from '@jbhi-fi/lanyard-ui/components/Carousel';
import { styles } from './CustomRefinementListContainer.css';
import { Swiper } from 'swiper';
import { useBreakpoint } from '@jbhi-fi/lanyard-ui/utils/useBreakpoint';
import { pushToDataLayer } from '@jbhi-fi/utils/src/ga4Tracking/datalayer';
import { palette } from '@jbhi-fi/lanyard-ui/themes';
import type { SearchState } from '../collections/searchStateUrlStorageHelpers';
import { getCollectionHandleFromPath } from './utils';
import type { TrackingInfo } from '../../../types/search';

interface CustomRefinementListContainerProps {
  initialFacets: Array<{
    facet: string;
    type: string;
    title?: string;
  }>;
  priceRange: {
    min: number;
    max: number;
  };
  mobileShow: boolean;
  setMobileShowFilter: (mobileShow: boolean) => void;
  nbHits: number;
  trackingInfo: TrackingInfo;
  isSearchPage?: boolean;
  isSticky?: boolean;
  nbSaleItems: number;
  nbClearanceItems: number;
  /** When provided, renders each facet using this function instead of the default Algolia components */
  renderFacet?: (
    facet: string,
    type: string,
    index: number,
    setDisableScroll: (v: boolean) => void,
    onDropdownToggle: (isOpen: boolean) => void
  ) => ReactNode;
  nbRedHotDealItems?: number;
}

const CustomRefinementListContainer = ({
  initialFacets,
  priceRange,
  mobileShow,
  setMobileShowFilter,
  nbHits,
  trackingInfo,
  isSearchPage,
  isSticky,
  nbSaleItems,
  nbClearanceItems,
  renderFacet,
  nbRedHotDealItems,
}: CustomRefinementListContainerProps) => {
  const [disableScroll, setDisableScroll] = useState(false);
  const [facets, setFacets] = useState(initialFacets);
  const [openFilterIndex, setOpenFilterIndex] = useState<number | null>(null);
  const isDesktop = useBreakpoint('lg', 'up');
  const [swiper, setSwiper] = useState<Swiper | null>(null);

  useEffect(() => {
    setFacets([...initialFacets]);
  }, [initialFacets]);

  // Remove scrolling on mobile.
  useEffect(() => {
    if (mobileShow) {
      document.body.classList.add('no-scroll');
    } else {
      document.body.classList.remove('no-scroll');
    }
  }, [mobileShow]);

  useEffect(() => {
    if (!swiper) {
      return;
    }

    swiper.update();

    if (swiper?.el) {
      if (disableScroll) {
        swiper.disable();
      } else {
        swiper.enable();
      }
    }
  }, [swiper, nbHits, disableScroll]);

  const sortedFacets = [...facets].sort(a => (a.type === 'toggleRefinement' ? -1 : 1)); // Place toggle buttons at the front of the slider.

  const onToggle = (facet: string, isChecked: boolean) => {
    if (facet !== 'isMarketplace') return;
    const searchState = qs.parse(window.location.search.slice(1)) as SearchState;
    const query = typeof searchState?.query === 'string' ? searchState.query : '';
    const searchType = isSearchPage ? 'site search' : 'collection';
    const searchTerm = isSearchPage ? query : getCollectionHandleFromPath(window.location.pathname);

    pushToDataLayer({
      event: isChecked ? 'select_filter' : 'unselect_filter',
      filter_group: 'Exclude Marketplace products',
      filter_name: 'undefined',
      filter_type: 'toggle',
      filter_value: isChecked ? 'Y' : 'N',
      search_type: searchType,
      search_term: searchTerm,
    });

    if (isChecked) {
      setFacets(prev => prev.filter(f => f.facet !== 'Sold by'));
      return;
    }

    setFacets([...initialFacets]);
  };

  useEffect(() => {
    if (!swiper || openFilterIndex === null) {
      return;
    }
    swiper.slideTo(openFilterIndex);
  }, [openFilterIndex, swiper, nbHits]);

  const filters = sortedFacets.map(({ facet, type, title }, index) => {
    if (renderFacet) {
      return renderFacet(facet, type, index, setDisableScroll, isOpen => setOpenFilterIndex(isOpen ? index : null));
    }
    return type === 'rangeInput' || facet === 'Price' ? (
      <RangeSliderRefinement
        key={index}
        attribute={`facets.${facet}`}
        defaultRefinement={priceRange}
        facet={facet}
        trackingInfo={{ ...trackingInfo, position: index + 1 }}
        nbHits={nbHits}
        setDisableScroll={setDisableScroll}
        onDropdownToggle={isOpen => setOpenFilterIndex(isOpen ? index : null)}
        nbSaleItems={nbSaleItems}
        nbClearanceItems={nbClearanceItems}
        nbRedHotDealItems={nbRedHotDealItems}
        isSearchPage={isSearchPage}
      />
    ) : type === 'toggleRefinement' ? (
      <ToggleButtonRefinement
        key={index}
        facet={facet}
        valueWhenChecked={false}
        label={title ?? facet}
        onToggle={isChecked => onToggle(facet, isChecked)}
        isSearchPage={isSearchPage}
      />
    ) : (
      <CheckboxRefinement
        key={index}
        facet={facet}
        trackingInfo={{ ...trackingInfo, position: index + 1 }}
        limit={300}
        showMoreLimit={25}
        setDisableScroll={setDisableScroll}
        onDropdownToggle={isOpen => setOpenFilterIndex(isOpen ? index : null)}
        isSearchPage={isSearchPage}
      />
    );
  });
  // Render the slider.
  return (
    <div className={`facets-container ${mobileShow ? ' mobile-active' : ''}`} id="facets-container">
      {!isSearchPage || !isDesktop ? (
        <>
          <button className="mobile-close" onClick={() => setMobileShowFilter(false)} />
          <h3 className="mobile-title">Filter</h3>
        </>
      ) : null}
      {isDesktop && !isSearchPage ? (
        <Carousel
          onSwiper={swiper => {
            setSwiper(swiper);
          }}
          showPagination={false}
          showProgressBar={false}
          showArrows={true}
          arrowPosition="middle"
          slidesPerView="auto"
          containerClassName={styles.container({ isSticky })}
          allowTouchMove={!disableScroll}
          slideArrowClassName={styles.slideArrow}
          arrowBackgroundColor={isSticky ? palette.secondary.main : palette.primary.light}
          hideArrowBackgroundColor={false}
        >
          {filters}
        </Carousel>
      ) : (
        <>{filters}</>
      )}
      <div className={styles.bottomHide} />
      <Button
        classes="ais-ShowResults mobile-show"
        type="primary"
        onClick={() => setMobileShowFilter(false)}
        label="Show results"
      />
    </div>
  );
};

export default CustomRefinementListContainer;
